import { createReducer } from 'redux-starter-kit';
import { handleError } from '../../utils/handleError';
import i18next from 'i18next';
import Notice from '../../utils/Notice';
import { ComparePhotoApi } from './ComparePhotoApi';

/**
 * Constants
 */

export const comparePhotoModule = 'comparePhoto';
const LOADING_TABLE = `${comparePhotoModule}/LOADING_TABLE`;
const LOADING_RESULT = `${comparePhotoModule}/LOADING_RESULT`;
const TABLE_DATA = `${comparePhotoModule}/TABLE_DATA`;
const CLEAR_STATE = `${comparePhotoModule}/CLEAR_STATE`;
const SET_DDOS = `${comparePhotoModule}/SET_DDOS`;
const SET_RESULTS = `${comparePhotoModule}/SET_RESULTS`;
const SET_META = `${comparePhotoModule}/SET_META`;
const LOADING_PASSPORT = `${comparePhotoModule}/LOADING_PASSPORT`;
const SET_FIRST_CONTINGENT_DATA = `${comparePhotoModule}/SET_FIRST_CONTINGENT_DATA`;
const SET_SECOND_CONTINGENT_DATA = `${comparePhotoModule}/SET_SECOND_CONTINGENT_DATA`;

/**
 * Reducer
 */

const initialState = {
  loadingPassport: false,
  loadingTable: false,
  loadingResult: false,
  contingentIds: {
    content: [],
    totalElements: 0
  },
  tableData: {
    content: [],
    totalElements: 0
  },
  firstContingent: {},
  firstMeta: {},
  firstInfo: {},

  secondContingent: {},
  secondMeta: {},
  secondInfo: {},
  ddos: {
    content: [],
    totalElements: 0
  }
};

export default createReducer(initialState, {
  [LOADING_PASSPORT]: (state, action) => {
    state.loadingPassport = action.payload;
  },
  [LOADING_TABLE]: (state, action) => {
    state.loadingTable = action.payload;
  },
  [LOADING_RESULT]: (state, action) => {
    state.loadingResult = action.payload;
  },
  [TABLE_DATA]: (state, action) => {
    const filter = action.filter || {};
    if (filter.pageNumber && filter.pageNumber !== 1) {
      state.tableData.content = [...state.tableData.content, ...action.payload.content];
    } else {
      state.tableData = action.payload;
    }
  },
  [SET_RESULTS]: (state, action) => {
    state.contingentIds=action.payload;
  },
  [CLEAR_STATE]: () => initialState,
  [SET_DDOS]: (state, action) => {
    const filter = action.filter || {};
    if (filter.pageNumber && filter.pageNumber !== 1) {
      state.ddos.content = [...state.ddos.content, ...action.payload.content];
    } else {
      state.ddos = action.payload;
    }
  },
  [SET_META]: (state, action) => {
    state.meta = action.payload;
  },
  [SET_FIRST_CONTINGENT_DATA]: (state, action) => {
    const { meta, data, info } = action.payload;
    if (meta) state.firstMeta = meta;
    if (info) state.firstInfo = info;
    if (data) state.firstContingent = data;
  },
  [SET_SECOND_CONTINGENT_DATA]: (state, action) => {
    const { meta, data, info } = action.payload;
    if (meta) state.secondMeta = meta;
    if (info) state.secondInfo = info;
    if (data) state.secondContingent = data;
  },
  [CLEAR_STATE]: () => initialState
});

/**
 * Actions
 */
export const getDDOs = fFilter => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });
    const filter = { ...fFilter };
    if (!filter.hasOwnProperty('extraFilters')) {
      filter['extraFilters'] = {};
    } else {
      filter['extraFilters'] = JSON.parse(filter['extraFilters']);
    }
    const customFilters = {
      ...filter,
      ...filter['extraFilters'],
      serviceProviderType: '1',
      nameRu: filter.ruName,
      nameKk: filter.kkName
    };

    delete customFilters['extraFilters'];
    delete customFilters['ruName'];
    delete customFilters['kkName'];

    const { data } = await ComparePhotoApi.getDDO(JSON.stringify(customFilters));
    dispatch({
      type: SET_DDOS,
      payload: data.result,
      filter: fFilter
    });
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorGetDDOs'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const clearState = () => ({ type: CLEAR_STATE });

export const loadTableData = filter => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });
    let { data } = await ComparePhotoApi.loadTasks(JSON.stringify(filter));
    if (data.status === 'SUCCESS') {
      dispatch({ type: TABLE_DATA, payload: data.result, filter });
    } else {
      handleError(data, i18next.t('comparePhoto_errorLoadTable'));
    }
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const orderUp = (taskId, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const { data } = await ComparePhotoApi.orderUp(taskId);
    if (data.status === 'SUCCESS') {
      Notice.success(i18next.t('comparePhoto_successOrderUp'));
      await dispatch(loadTableData(filter));
    }
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorOrderUp'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};
export const orderDown = (taskId, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const { data } = await ComparePhotoApi.orderDown(taskId);
    if (data.status === 'SUCCESS') {
      Notice.success(i18next.t('comparePhoto_successOrderDown'));
      await dispatch(loadTableData(filter));
    }
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorOrderDown'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const deleteOrder = (taskId, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const { data } = await ComparePhotoApi.deleteOrder(taskId);
    if (data.status === 'SUCCESS') {
      Notice.success(i18next.t('comparePhoto_successDeleteOrder'));
      await dispatch(loadTableData(filter));
    }
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorDeleteOrder'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const createOrder = (serviceProviderId, isFirst, close) => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const { data } = await ComparePhotoApi.createOrder({
      serviceProviderId,
      first: isFirst
    });
    const filter = {
      pageNumber: 1,
      pageSize: 20
    };
    if (data.status === 'SUCCESS') {
      Notice.success(i18next.t('comparePhoto_successCreateOrder'));
      await dispatch(loadTableData(filter));
      close();
      close();
    }
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorCreateOrder'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};
export const getResults = (taskId, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING_RESULT, payload: true });
    const filters = JSON.stringify({
      ...filter,
      taskId
    });
    const { data } = await ComparePhotoApi.getResultFromCompletedTask(filters);
    dispatch({ type: SET_RESULTS, payload: data.result.result, filter });
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorGetResults'));
  } finally {
    dispatch({ type: LOADING_RESULT, payload: false });
  }
};
export const setData = ({ meta, data, isFirst }) => async dispatch => {
  const info = {
    id: data.id,
    iin: data.iin
  };
  isFirst
    ? dispatch({ type: SET_FIRST_CONTINGENT_DATA, payload: { meta, data, info } })
    : dispatch({ type: SET_SECOND_CONTINGENT_DATA, payload: { meta, data, info } });
  dispatch({ type: LOADING_PASSPORT, payload: false });
};

export const getContingentItem = (id, isFirst) => async dispatch => {
  try {
    dispatch({ type: LOADING_PASSPORT, payload: true });
    const { data } = await ComparePhotoApi.getContingentItem(id);
    const { data: meta } = await ComparePhotoApi.loadMeta(data.metadataKey);
    dispatch(setData({ meta, data, isFirst }));
  } catch (error) {
    handleError(error, i18next.t('comparePhoto_errorLoadMeta'));
  } finally {
    dispatch({ type: LOADING_PASSPORT, payload: false });
  }
};
